import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { withTranslation } from "react-i18next";
import Icon from "../shared/Icon";
import { isAbortError } from "../../../services/managerApi";
import useManagerScope from "../../../hooks/useManagerScope";
import A4Preview from "./A4Preview";
import ResidentsPanel from "./ResidentsPanel";
import CsvImportButton from "./CsvImportButton";
import { createOrdersSource } from "./residentsSource";
import { LABELS_PER_PAGE } from "./labelLayout";
import { generateFullPagesPDF, generateLabelsPDF, openOrDownloadPdf } from "./pdfGenerator";
import "../manager.css";
import "./labels.css";

/**
 * Écran Etiketten — planches d'étiquettes résidents, A4 24 poses.
 *
 * Cet écran ne connaît pas le réseau : il reçoit `fetchResidents` en prop et se
 * contente de l'appeler. La valeur par défaut lit les commandes du point de
 * collecte (voir `residentsSource.js`), mais on peut injecter n'importe quoi —
 * un autre endpoint, un selector Redux, un tableau de test.
 *
 * La liste chargée reste entièrement éditable : import CSV et saisie manuelle
 * viennent la compléter ou la remplacer.
 *
 * Les étiquettes ne portent jamais de montant — elles identifient le linge d'une
 * personne, rien de plus.
 */

let nextId = 1;
const makeId = () => `lbl-${nextId++}`;

const normalizeEntry = (raw) => ({
  id: makeId(),
  woshId: String(raw.woshId || "").trim(),
  firstName: String(raw.firstName || "").trim(),
  lastName: String(raw.lastName || "").trim(),
  residence: String(raw.residence || "").trim(),
  pages: Math.max(1, Math.min(50, Number(raw.pages) || 1)),
});

const Etiketten = ({ t, authUser, fetchResidents }) => {
  const [entries, setEntries] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [mode, setMode] = useState("fullPages");
  const [cutMarks, setCutMarks] = useState(false);
  const [cells, setCells] = useState(() => Array(LABELS_PER_PAGE).fill(null));
  const [isBusy, setIsBusy] = useState(false);
  const [csvError, setCsvError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // La source par défaut dépend de `authUser` : la mémoriser évite de relancer
  // le chargement à chaque rendu, sans forcer l'appelant à la mémoriser lui-même.
  // La résidence par défaut est l'établissement du manager : sur une planche,
  // elle est identique pour tout le monde neuf fois sur dix. Elle reste vide
  // tant que le serveur ne renvoie pas le périmètre du manager — rien d'autre
  // ne porte cette information, et un nom deviné finirait imprimé.
  const { establishmentName, collectionPoints } = useManagerScope(authUser);
  const residenceName = establishmentName;
  const residenceOptions = useMemo(
    () => (collectionPoints || []).map((point) => point.name).filter(Boolean),
    [collectionPoints]
  );

  const defaultSource = useMemo(() => createOrdersSource(authUser), [authUser]);
  const source = fetchResidents || defaultSource;

  // La liste est éditable : un rechargement ne doit pas écraser silencieusement
  // ce que l'utilisateur a saisi. On ne charge donc qu'une fois par source.
  const loadedSource = useRef(null);

  const appendRows = useCallback(
    (rows) => {
      const normalized = rows.map((row) =>
        normalizeEntry({
          ...row,
          // Le CSV porte sa propre colonne Residence ; sinon on retombe sur
          // l'établissement du manager.
          residence: row.residence || residenceName,
        })
      );
      setEntries((current) => [...current, ...normalized]);
      if (normalized.length > 0) setActiveId(normalized[0].id);
      return normalized.length;
    },
    [residenceName]
  );

  useEffect(() => {
    if (loadedSource.current === source) return;
    loadedSource.current = source;

    const controller = new AbortController();
    let active = true;

    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const rows = await source({ signal: controller.signal });
        // Les résidents chargés alimentent l'annuaire, pas la liste à imprimer :
        // on imprime pour quelqu'un qu'on choisit, on ne déverse pas tout le
        // point de collecte dans la file d'impression.
        if (active) setDirectory(rows || []);
      } catch (caught) {
        if (active && !isAbortError(caught)) setError(caught);
      } finally {
        if (active) setIsLoading(false);
      }
    })();

    return () => {
      active = false;
      controller.abort();
    };
    // `appendRows` n'est volontairement pas listé : l'effet ne s'en sert plus
    // depuis que l'annuaire est séparé de la file d'impression. L'y laisser
    // relançait l'effet quand le nom d'établissement arrivait du profil : le
    // nettoyage annulait la requête en cours sans jamais rendre la main, puis
    // le garde `loadedSource` coupait court — et l'écran restait en chargement
    // indéfiniment.
  }, [source]);

  const addEntry = (raw) => {
    const entry = normalizeEntry(raw);
    setEntries((current) => [...current, entry]);
    setActiveId(entry.id);
  };

  const importEntries = (rows) => {
    appendRows(rows);
  };

  const removeEntry = (id) => {
    setEntries((current) => current.filter((entry) => entry.id !== id));
    setCells((current) => current.map((cell) => (cell && cell.id === id ? null : cell)));
    setActiveId((current) => (current === id ? null : current));
  };

  /** Forme consommée par l'aperçu et par le PDF. */
  const toLabel = useCallback(
    (entry) => ({
      id: entry.id,
      woshId: entry.woshId,
      firstName: entry.firstName,
      lastName: entry.lastName,
      residence: entry.residence,
      pages: entry.pages,
    }),
    []
  );

  const addedIds = useMemo(
    () => new Set(entries.map((entry) => entry.woshId)),
    [entries]
  );

  const pickResident = (resident) => {
    addEntry({
      woshId: resident.woshId,
      firstName: resident.firstName,
      lastName: resident.lastName,
      residence: residenceName,
      pages: 1,
    });
  };

  const totalSheets = entries.reduce((sum, entry) => sum + entry.pages, 0);
  const filledCells = cells.filter(Boolean).length;

  const handleToggleCell = (index) => {
    setCells((current) => {
      const next = [...current];
      if (next[index]) {
        next[index] = null;
        return next;
      }
      const entry = entries.find((item) => item.id === activeId);
      next[index] = entry ? toLabel(entry) : null;
      return next;
    });
  };

  const fillSheetWithActive = () => {
    const entry = entries.find((item) => item.id === activeId);
    if (!entry) return;
    setCells(Array(LABELS_PER_PAGE).fill(toLabel(entry)));
  };

  const handleGenerate = async () => {
    setIsBusy(true);
    setError(null);
    try {
      const doc =
        mode === "fullPages"
          ? await generateFullPagesPDF(entries.map(toLabel), { cutMarks })
          : await generateLabelsPDF(cells, { cutMarks });
      openOrDownloadPdf(doc, "wosh-etiketten.pdf");
    } catch (caught) {
      setError(caught);
    } finally {
      setIsBusy(false);
    }
  };

  const canGenerate = !isBusy && (mode === "fullPages" ? entries.length > 0 : filledCells > 0);

  // L'aperçu montre la planche du résident sélectionné, ou la planche entamée.
  const previewCells = useMemo(() => {
    if (mode === "ecoSheet") return cells;
    if (entries.length === 0) return Array(LABELS_PER_PAGE).fill(null);
    const first = entries.find((entry) => entry.id === activeId) || entries[0];
    return Array(LABELS_PER_PAGE).fill(toLabel(first));
  }, [mode, cells, entries, activeId, toLabel]);

  return (
    <div className="mgr-lbl">
      <div className="mgr-head">
        <h1 className="mgr-h1">{t("Etiketten")}</h1>
        <div className="mgr-lbl-actions">
          <CsvImportButton onImport={importEntries} onError={setCsvError} t={t} />
          <button
            type="button"
            className="mgr-btn-primary"
            onClick={handleGenerate}
            disabled={!canGenerate}
          >
            <Icon name="qr-code-outline" />
            {isBusy ? t("Generating…") : t("Print labels")}
          </button>
        </div>
      </div>

      {error || csvError ? (
        <p role="alert" className="mgr-lbl-error mgr-lbl-error--top">
          {csvError || error.message || t("Something went wrong.")}
        </p>
      ) : null}

      <div className="mgr-lbl-cols">
        <section className="mgr-card mgr-card-pad">
          <ResidentsPanel
            entries={entries}
            directory={directory}
            addedIds={addedIds}
            onPick={pickResident}
            residenceName={residenceName}
            residenceOptions={residenceOptions}
            activeId={activeId}
            mode={mode}
            isLoading={isLoading}
            onAdd={addEntry}
            onImport={importEntries}
            onSelect={setActiveId}
            onRemove={removeEntry}
            t={t}
          />
        </section>

        <section className="mgr-card mgr-card-pad">
          <div className="mgr-label">{t("Options")}</div>

          <div className="mgr-lbl-modes" role="group">
            <button
              type="button"
              className={`mgr-btn-pill${mode === "fullPages" ? " is-active" : ""}`}
              onClick={() => setMode("fullPages")}
            >
              {t("Full sheets")}
            </button>
            <button
              type="button"
              className={`mgr-btn-pill${mode === "ecoSheet" ? " is-active" : ""}`}
              onClick={() => setMode("ecoSheet")}
            >
              {t("Partly used sheet")}
            </button>
          </div>

          <label className="mgr-lbl-check">
            <input
              type="checkbox"
              checked={cutMarks}
              onChange={(event) => setCutMarks(event.target.checked)}
            />
            <span>{t("Cut marks")}</span>
          </label>

          {mode === "ecoSheet" ? (
            <div className="mgr-lbl-eco-tools">
              <button
                type="button"
                className="mgr-btn-ghost"
                onClick={fillSheetWithActive}
                disabled={!activeId}
              >
                {t("Fill sheet")}
              </button>
              <button
                type="button"
                className="mgr-btn-ghost"
                onClick={() => setCells(Array(LABELS_PER_PAGE).fill(null))}
                disabled={filledCells === 0}
              >
                {t("Clear sheet")}
              </button>
            </div>
          ) : null}

          <div className="mgr-lbl-summary">
            {mode === "fullPages"
              ? `${entries.length} ${t("labels")} · ${totalSheets} ${t("sheets")}`
              : `${filledCells} / ${LABELS_PER_PAGE} ${t("labels")}`}
          </div>
        </section>

        <section className="mgr-card mgr-card-pad">
          <div className="mgr-label">{t("Preview")}</div>
          <A4Preview
            cells={previewCells}
            onToggleCell={mode === "ecoSheet" ? handleToggleCell : undefined}
            t={t}
          />
        </section>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  authUser: state?.auth?.user,
});

export default compose(withTranslation(), connect(mapStateToProps))(Etiketten);
